/**
 * snapshotFile
 *
 * Reads a File object into an in-memory ArrayBuffer/Blob immediately to avoid
 * issues with detached/revoked file handles from mobile cloud pickers (Google Drive, iCloud, WhatsApp).
 *
 * @param {File} file
 * @returns {Promise<{ file: File | null, bytes: number, error: Error | null, skippedReason?: string }>}
 */
export async function snapshotFile(file) {
  if (!file) {
    return { file: null, bytes: 0, error: new Error('No file provided') };
  }

  if (file.size === 0) {
    return { file: null, bytes: 0, error: new Error('File is 0 bytes'), skippedReason: 'empty_file' };
  }

  try {
    let buffer;
    if (typeof file.arrayBuffer === 'function') {
      buffer = await file.arrayBuffer();
    } else {
      buffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('FILE_READ_FAILED'));
        reader.readAsArrayBuffer(file);
      });
    }

    if (!buffer || buffer.byteLength === 0) {
      return { file: null, bytes: 0, error: new Error('File buffer is empty'), skippedReason: 'zero_bytes_read' };
    }

    const snapFile = new File([buffer], file.name || 'document', {
      type: file.type || 'application/octet-stream',
      lastModified: file.lastModified || Date.now(),
    });

    return { file: snapFile, bytes: buffer.byteLength, error: null };
  } catch (err) {
    return { file: null, bytes: 0, error: err, skippedReason: err?.name || 'read_exception' };
  }
}
